import React, { createContext, useContext, useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { AuthState, User, Session, SignInCredentials, ResetPasswordCredentials, UpdatePasswordCredentials, AuthError } from '@/types/auth'

interface AuthContextType extends AuthState {
  signIn: (credentials: SignInCredentials) => Promise<{ error: AuthError | null }>
  signOut: () => Promise<{ error: AuthError | null }>
  resetPassword: (credentials: ResetPasswordCredentials) => Promise<{ error: AuthError | null }>
  updatePassword: (credentials: UpdatePasswordCredentials) => Promise<{ error: AuthError | null }>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const location = useLocation()

  console.log('🔐 AuthProvider: Rendering', { loading, hasUser: !!user, hasSession: !!session })

  useEffect(() => {
    let mounted = true

    console.log('🔐 AuthProvider: useEffect triggered')

    // Helper function to check if current route is public
    const isPublicRoute = () => {
      const publicRoutes = ['/login', '/reset-password', '/update-password']
      const isPublic = publicRoutes.some(route =>
        location.pathname === route ||
        location.pathname.startsWith(route + '/')
      )
      console.log('🔐 AuthProvider: isPublicRoute check', { pathname: location.pathname, isPublic })
      return isPublic
    }

    // Get initial session
    let timeoutId: NodeJS.Timeout | null = null

    const getInitialSession = async () => {
      console.log('🔐 AuthProvider: getInitialSession called')

      if (!supabase) {
        console.warn('⚠️ Supabase client not configured. Check VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY environment variables.')
        if (mounted) {
          setLoading(false)
        }
        // Clear timeout if it exists
        if (timeoutId) {
          clearTimeout(timeoutId)
        }
        return
      }

      try {
        console.log('🔐 AuthProvider: Calling supabase.auth.getSession()')
        const { data: { session }, error } = await supabase.auth.getSession()
        console.log('🔐 AuthProvider: getSession response', { hasSession: !!session, error: error?.message })

        if (!mounted) {
          console.log('🔐 AuthProvider: Component unmounted, skipping state update')
          return
        }

        // Clear timeout since we got a response
        if (timeoutId) {
          clearTimeout(timeoutId)
          timeoutId = null
        }

        if (error) {
          console.error('❌ AuthProvider: Error getting session:', error)
        } else {
          console.log('🔐 AuthProvider: Setting session and user', { hasSession: !!session, hasUser: !!session?.user })
          setSession(session)
          setUser(session?.user ?? null)

          // Check if user is not authenticated and not on a public route
          if (!session && !isPublicRoute()) {
            console.log('🔐 AuthProvider: No session and not on public route, redirecting to /login')
            setTimeout(() => {
              if (mounted) {
                navigate('/login', { replace: true })
              }
            }, 0)
          }
        }
      } catch (error) {
        console.error('❌ AuthProvider: Error in getInitialSession:', error)
        // Clear timeout on error
        if (timeoutId) {
          clearTimeout(timeoutId)
          timeoutId = null
        }
      } finally {
        if (mounted) {
          console.log('🔐 AuthProvider: Setting loading to false')
          setLoading(false)
        }
      }
    }

    // For public pages, set loading to false immediately and skip timeout
    if (isPublicRoute()) {
      console.log('🔐 AuthProvider: Public route detected, setting loading to false immediately')
      setLoading(false)
      // Still get session in background, but don't block
      getInitialSession()
    } else {
      // Set a timeout only for protected pages
      const timeout = 3000
      console.log('🔐 AuthProvider: Setting timeout', { timeout, isPublic: false })

      timeoutId = setTimeout(() => {
        if (mounted) {
          console.warn('⚠️ AuthProvider: Auth initialization timeout - setting loading to false')
          setLoading(false)
          timeoutId = null
        }
      }, timeout)

      getInitialSession()
    }

    // Listen for auth changes
    let subscription: { unsubscribe: () => void } | null = null

    if (supabase) {
      try {
        console.log('🔐 AuthProvider: Setting up auth state change listener')
        const authStateChange = supabase.auth.onAuthStateChange(
          async (event, session) => {
            console.log('🔐 AuthProvider: Auth state changed', { event, hasSession: !!session })
            if (!mounted) {
              console.log('🔐 AuthProvider: Component unmounted during auth state change')
              return
            }
            setSession(session)
            setUser(session?.user ?? null)
            setLoading(false)

            // Redirect to login page on sign out or when session becomes null
            if (event === 'SIGNED_OUT' || (!session && !isPublicRoute())) {
              console.log('🔐 AuthProvider: Redirecting to /login due to auth state change', { event })
              setTimeout(() => {
                if (mounted) {
                  navigate('/login', { replace: true })
                }
              }, 0)
            }
          }
        )

        subscription = authStateChange.data.subscription
        console.log('🔐 AuthProvider: Auth state change listener set up')
      } catch (error) {
        console.error('❌ AuthProvider: Error setting up auth state change listener:', error)
        if (mounted) {
          setLoading(false)
        }
      }
    } else {
      console.log('⚠️ AuthProvider: Supabase not configured, setting loading to false')
      // If Supabase is not configured, just set loading to false
      if (mounted) {
        setLoading(false)
      }
    }

    return () => {
      mounted = false
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      subscription?.unsubscribe()
    }
  }, [navigate, location.pathname])

  const signIn = async (credentials: SignInCredentials): Promise<{ error: AuthError | null }> => {
    if (!supabase) {
      return { error: { message: 'Supabase client not configured' } }
    }

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      })

      if (error) {
        return { error: { message: error.message, code: error.message } }
      }

      return { error: null }
    } catch (error) {
      return { error: { message: 'An unexpected error occurred' } }
    }
  }

  const signOut = async (): Promise<{ error: AuthError | null }> => {
    if (!supabase) {
      return { error: { message: 'Supabase client not configured' } }
    }

    try {
      // Use scope: 'local' to only sign out from the current browser session
      // This is more reliable than 'global' which can fail with 403/483 errors
      // when the session is already invalid on the server
      const { error } = await supabase.auth.signOut({ scope: 'local' })

      // Even if there's an error (e.g., 403 Forbidden due to expired session),
      // the local session should be cleared by the auth state change listener
      if (error) {
        console.warn('⚠️ Sign out error (will still clear local session):', error.message)
        // Manually clear the session locally if signOut fails
        // This ensures onAuthStateChange triggers and redirects to login
        await supabase.auth.setSession({ access_token: '', refresh_token: '' })
        // Explicitly navigate to login since onAuthStateChange might not trigger
        navigate('/login', { replace: true })
      }

      return { error: null }
    } catch (error) {
      console.error('❌ Unexpected error during sign out:', error)
      // Even on exception, clear the session to ensure redirect
      try {
        await supabase.auth.setSession({ access_token: '', refresh_token: '' })
      } catch (e) {
        console.error('❌ Failed to clear session:', e)
      }
      // Explicitly navigate to login
      navigate('/login', { replace: true })
      return { error: null }
    }
  }

  const resetPassword = async (credentials: ResetPasswordCredentials): Promise<{ error: AuthError | null }> => {
    if (!supabase) {
      return { error: { message: 'Supabase client not configured' } }
    }

    try {
      console.log('🔄 AuthContext: Requesting password reset', {
        email: credentials.email,
        redirectTo: `${window.location.origin}/update-password`
      })

      const { error } = await supabase.auth.resetPasswordForEmail(credentials.email, {
        redirectTo: `${window.location.origin}/update-password`,
      })

      if (error) {
        return { error: { message: error.message, code: error.message } }
      }

      return { error: null }
    } catch (error) {
      return { error: { message: 'An unexpected error occurred' } }
    }
  }

  const updatePassword = async (credentials: UpdatePasswordCredentials): Promise<{ error: AuthError | null }> => {
    if (!supabase) {
      return { error: { message: 'Supabase client not configured' } }
    }

    if (credentials.password !== credentials.confirmPassword) {
      return { error: { message: 'Passwords do not match' } }
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: credentials.password
      })

      if (error) {
        return { error: { message: error.message, code: error.message } }
      }

      return { error: null }
    } catch (error) {
      return { error: { message: 'An unexpected error occurred' } }
    }
  }

  const value: AuthContextType = {
    user,
    session,
    loading,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
